import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import React, { FunctionComponent, useContext } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import ROUTES from '../../../routes';
import colors from '../../../themes/main-colors';
import Link from '../../text-link';
import {
  toDateString,
  toCurrencyString,
  toCurrencyDisplayFormat,
  toSentenceCase,
} from '../../../util/format';
import {
  BillInfo,
  AutoPayInfo,
  ViewBillDetails,
} from '../../../__generated__/pge-types';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import useGTMUtilAcctSummary from '../gtm/useGTMUtilAcctSummary';
import { LanguageContext } from '../../../providers/LanguageProvider';

export interface CurrentBillPaymentSummaryProps {
  amountCreditDisplay: 'CR' | 'Zero';
  autoPay: AutoPayInfo | null;
  billInfo: BillInfo | null | undefined;
  isMobile: boolean;
  viewBillDetails: ViewBillDetails | null | undefined;
  isPendingDisconnect: boolean | undefined;
}

function buildPaymentMessage(billInfo: BillInfo | null | undefined) {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const {
    oneTimeFuturePaymentScheduled,
    multipleFuturePaymentsScheduled,
    lastPaymentDate,
    lastPaymentAmount,
    futurePaymentDate,
  } = billInfo || {};

  if (oneTimeFuturePaymentScheduled && Boolean(futurePaymentDate)) {
    const futureMessage = t('LAST_PAYMENT_ONE_FUTURE', {
      AMOUNT_PAID: toCurrencyString(lastPaymentAmount || 0, false),
      DATE_OF_PAYMENT: toDateString(lastPaymentDate || ''),
      FUTURE_AMOUNT: toCurrencyString(lastPaymentAmount || 0, false),
      FUTURE_DATE: toDateString(futurePaymentDate || ''),
    });

    if (Boolean(lastPaymentAmount) && Boolean(lastPaymentDate)) {
      return `${t('LAST_PAYMENT_OF', {
        AMOUNT_PAID: toCurrencyString(lastPaymentAmount || 0, false),
        DATE_OF_PAYMENT: toDateString(
          lastPaymentDate || '',
          'MMM. dd, yyyy',
          language,
        ),
      })} ${futureMessage}`;
    } else {
      return futureMessage;
    }
  } else if (multipleFuturePaymentsScheduled) {
    return t('LAST_PAYMENT_MULTIPLE_FUTURE');
  } else if (Boolean(lastPaymentAmount) && Boolean(lastPaymentDate)) {
    return t('LAST_PAYMENT_OF', {
      AMOUNT_PAID: toCurrencyString(lastPaymentAmount || 0, false),
      DATE_OF_PAYMENT: toDateString(
        lastPaymentDate || '',
        'MMM. dd, yyyy',
        language,
      ),
    });
  }

  return t('LAST_PAYMENT_YET_TO_BE_BILLED');
}

function buildPaymentDate(
  billInfo: BillInfo | null | undefined,
  autoPay: AutoPayInfo | null,
) {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const isEnrolledInAutopay = Boolean(autoPay?.isEnrolled);
  if (isEnrolledInAutopay && autoPay?.draftDate) {
    return {
      title: t('WILL_BE_DEDUCTED'),
      value:
        Number(billInfo?.amountDue) > 0 && billInfo?.dueDate
          ? toDateString(billInfo?.dueDate, 'MMM. dd, yyyy', language)
          : '---',
    };
  } else {
    return {
      title: t('DUE_DATE'),
      value:
        Number(billInfo?.amountDue) > 0 && billInfo?.dueDate
          ? toDateString(billInfo?.dueDate, 'MMM. dd, yyyy', language)
          : 'Not yet available',
    };
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
    },
    colorTextPrimary: {
      color: colors.noirBlur,
    },
    colorTextPastdue: {
      color: colors.roseRed,
      fontWeight: 'bold',
    },
    containerRow: {
      margin: theme.spacing(2, 0, 2, 0),
    },
    boxContainer: {
      backgroundColor: colors.gray93,
      padding: theme.spacing(0, 1, 1, 1),
    },
  }),
);

const CurrentBillPaymentSummary: FunctionComponent<CurrentBillPaymentSummaryProps> = ({
  billInfo,
  autoPay,
  amountCreditDisplay,
  viewBillDetails,
  isPendingDisconnect,
  isMobile,
}) => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const { gtm_CurrentBill_NavigateToNMTTP } = useGTMUtilAcctSummary();
  const message = buildPaymentMessage(billInfo);
  const dueDateInfo = buildPaymentDate(billInfo, autoPay);
  const currentPath = location.pathname;

  const contents = (
    <Grid container direction="column">
      <Grid item direction="column">
        <Grid item style={{ marginBottom: '8px' }} xs={12}>
          <Typography>{t('CURRENT_BILL_PAY_BILL_TEXT')}</Typography>
        </Grid>

        <Grid item className={classes.containerRow} xs={12}>
          <Grid container direction="row" className={classes.boxContainer}>
            <Grid item xs={12}>
              {viewBillDetails && viewBillDetails.billingPeriodStartDate ? (
                <Typography
                  className={classes.colorTextPrimary}
                  variant="h6"
                  data-testid="service-startdate-enddate-message"
                >
                  {richT('SERVICE_FROM_START_END_DATE', {
                    START_DATE: toDateString(
                      viewBillDetails.billingPeriodStartDate,
                      'MM/dd/yyyy',
                    ),
                    END_DATE: toDateString(
                      viewBillDetails.billingPeriodEndDate,
                      'MM/dd/yyyy',
                    ),
                  })}
                </Typography>
              ) : (
                <Typography
                  className={classes.colorTextPrimary}
                  variant="h6"
                  data-testid="no-service-message"
                >
                  {richT('SERVICE_NOT_AVAILABLE')}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid container direction="row" className={classes.containerRow}>
              <Grid item xs={isMobile ? 12 : 6}>
                {isPendingDisconnect ? (
                  <Typography
                    className={classes.colorTextPastdue}
                    data-testid="past-due-label"
                    variant="h1"
                  >
                    {t('PAST_DUE') + ':'}
                  </Typography>
                ) : Boolean(autoPay?.isEnrolled) ? (
                  <Typography
                    className={classes.colorTextPrimary}
                    data-testid="auto-pay-label"
                  >
                    {t('AUTO_PAY_AMOUNT')}
                  </Typography>
                ) : (
                  <Typography
                    className={classes.colorTextPrimary}
                    data-testid="amount-due-label"
                  >
                    {t('AMOUNT_DUE') + ':'}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={isMobile ? 12 : 6}>
                <Typography
                  className={
                    isPendingDisconnect
                      ? classes.colorTextPastdue
                      : classes.colorTextPrimary
                  }
                  variant="h2"
                  style={{ fontWeight: 'bold' }}
                >
                  {`${
                    billInfo?.amountDue !== null &&
                    billInfo?.amountDue !== undefined
                      ? '$' +
                        toCurrencyDisplayFormat(
                          billInfo?.amountDue,
                          true,
                          amountCreditDisplay,
                        )
                      : '--'
                  }`}
                </Typography>
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item xs={isMobile ? 12 : 6}>
                <Typography
                  className={
                    isPendingDisconnect
                      ? classes.colorTextPastdue
                      : classes.colorTextPrimary
                  }
                >
                  {toSentenceCase(dueDateInfo.title) + ':'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  data-testid="current-bill-pay-summary-due-date"
                  style={{ fontWeight: 'bold' }}
                  className={
                    isPendingDisconnect
                      ? classes.colorTextPastdue
                      : classes.colorTextPrimary
                  }
                >
                  {dueDateInfo.value}
                </Typography>
              </Grid>
            </Grid>
            {isPendingDisconnect && (
              <Grid direction="row" style={{ marginTop: '0.5em' }}>
                <Typography
                  className={classes.colorTextPrimary}
                  variant="body2"
                  data-testid="past-due-message"
                >
                  {t('PAST_DUE_MESSAGE', {
                    CURRENT_CHARGES: String(
                      viewBillDetails?.totalCurrentCharges,
                    ),
                    PAST_DUE: String(viewBillDetails?.totalBalanceAfterBill),
                  })}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        {Boolean(viewBillDetails) && (
          <Grid item xs={12} data-testid="need-more-time-to-pay-link">
            <Link
              plain
              to={ROUTES.NEED_MORE_TIME_TO_PAY}
              onClick={gtm_CurrentBill_NavigateToNMTTP}
            >
              {t('NMTTP')}
            </Link>
          </Grid>
        )}
        {Boolean(viewBillDetails) && (
          <Grid
            item
            xs={12}
            style={{ marginTop: '0.5em' }}
            data-testid="payment-message"
          >
            <Typography variant="body2" style={{ fontSize: '1rem' }}>
              {message}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );

  return contents;
};
export default CurrentBillPaymentSummary;
